import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { pushGA } from '../lib/utils';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { session, signIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (session) navigate('/', { replace: true });
  }, [session, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error } = await signIn(email, password);
      
      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          setError('Email ou senha incorretos');
        } else if (error.message.includes('Email not confirmed')) {
          setError('Verifique seu email e clique no link de confirmação');
        } else if (error.message.includes('Network request failed')) {
          setError('Erro de conexão. Verifique sua internet.');
        } else {
          setError(`Erro: ${error.message}`);
        }
        console.error('Erro de login:', error);
      } else {
        console.log('Login realizado com sucesso!');
        pushGA('login_success', { email });
        window.location.replace('/');
        return;
      }
    } catch (err: any) {
      console.error('Erro inesperado no login:', err);
      setError('Erro inesperado. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray via-white to-light-gray-alt dark:from-soft-black dark:via-deep-purple/20 dark:to-soft-black font-exo flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 dark:opacity-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-darling-lilac via-transparent to-ocean-water" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-darling-lilac/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-ocean-water/20 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        {/* Logo e Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-darling-lilac/10 dark:bg-darling-lilac/20 mb-6">
            <Lock className="h-8 w-8 text-darling-lilac" />
          </div>
          
          <h1 className="text-3xl md:text-4xl font-exo font-black text-soft-black dark:text-white mb-2">
            <span className="bg-gradient-to-r from-darling-lilac to-ocean-water bg-clip-text text-transparent">
              CRO Brasil
            </span>
          </h1>
          
          <p className="text-soft-black/70 dark:text-light-gray/80 font-exo mb-2">
            Agenda de Embaixadores
          </p>
          
          <p className="text-sm text-soft-black/60 dark:text-light-gray/70 font-exo">
            Faça login para acessar a plataforma
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white/80 dark:bg-soft-black/80 backdrop-blur-sm rounded-2xl border border-light-gray-alt dark:border-deep-purple/30 shadow-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-exo font-medium text-soft-black dark:text-white mb-2">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-soft-black/40 dark:text-light-gray/50" />
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black text-soft-black dark:text-white font-exo focus:ring-2 focus:ring-darling-lilac/20 focus:border-darling-lilac/50 transition-all"
                  placeholder="seu@email.com"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-exo font-medium text-soft-black dark:text-white mb-2">
                Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-soft-black/40 dark:text-light-gray/50" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black text-soft-black dark:text-white font-exo focus:ring-2 focus:ring-darling-lilac/20 focus:border-darling-lilac/50 transition-all"
                  placeholder="Sua senha"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-soft-black/40 dark:text-light-gray/50 hover:text-darling-lilac transition-colors" />
                  ) : (
                    <Eye className="h-5 w-5 text-soft-black/40 dark:text-light-gray/50 hover:text-darling-lilac transition-colors" />
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50"
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <p className="text-sm text-red-600 dark:text-red-400 font-exo">
                    {error}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-darling-lilac hover:bg-deep-purple disabled:bg-darling-lilac/50 text-white font-exo font-semibold transition-all duration-200 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </button>
          </form>

          {/* Help Text */}
          <div className="mt-6 pt-6 border-t border-light-gray-alt dark:border-deep-purple/30 text-center">
            <p className="text-xs text-soft-black/50 dark:text-light-gray/60 font-exo mb-2">
              Não tem uma conta? O acesso é apenas por convite.
            </p>
            <p className="text-xs text-soft-black/50 dark:text-light-gray/60 font-exo">
              Entre em contato com a administração para receber um convite.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-soft-black/40 dark:text-light-gray/50 font-exo">
            © {new Date().getFullYear()} CRO Brasil — Plataforma de Embaixadores
          </p>
        </div>
      </motion.div>
    </div>
  );
}